import { countOf, formatEpoch, ownValue, tidy } from "../shared/safe";

// Pure parsing and honest summarising of the CREATED LINKS report (GET /v1/clients/:id/links and GET /v1/links/summary).
// Everything the server sends is untrusted: strings may hold markup, URLs may be `javascript:`, objects may be missing or the
// wrong type, a state may be one this page has never heard of. Nothing here touches the DOM.
//
// What the report is: the links CloudPush itself created (rendered or submitted), from its own receipts. It is not a list of
// backlinks found anywhere, and nothing here crawls. The counted number is "active" = delivered (not inspected) + observed.
// Planned, uncertain and absent links are listed but are NEVER added to a counted figure, and receipts from a previous account
// are history only.

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const str = (v: unknown): string | null => (typeof v === "string" && v.trim() !== "" ? v : null);

/** http(s) only, normalised; null for anything else (javascript:, data:, relative, garbage). */
export function httpUrl(value: unknown): string | null {
  if (typeof value !== "string") return null;
  try {
    const u = new URL(value.trim());
    return u.protocol === "http:" || u.protocol === "https:" ? u.href : null;
  } catch {
    return null;
  }
}

export interface UrlView {
  /** Bounded, control-character-free text exactly as the server wrote it. */
  text: string;
  /** Null when the value is not a safe http(s) URL: it is then shown as plain text, not as a link. */
  href: string | null;
}
const url = (v: unknown): UrlView | null => {
  const s = str(v);
  return s === null ? null : { text: tidy(s, 400), href: httpUrl(s) };
};

// ---- states -------------------------------------------------------------------------------------------------------------

export const LINK_STATES = ["delivered_uninspected", "observed", "planned", "uncertain", "absent"] as const;
export type LinkState = (typeof LINK_STATES)[number];
/** Only these two are counted ("active"). */
export const COUNTED_STATES: readonly LinkState[] = ["delivered_uninspected", "observed"];

export interface StateMeta {
  label: string;
  tone: "ok" | "warn" | "bad" | "neutral";
  counted: boolean;
  /** What this state proves, in plain words. */
  proves: string;
  /** What it does NOT prove. */
  doesNotProve: string;
}

export const STATE_META: Record<LinkState, StateMeta> = {
  delivered_uninspected: {
    label: "Delivered (not inspected)",
    tone: "neutral",
    counted: true,
    proves: "CloudPush recorded a delivery receipt from the provider for the current account, and the link is in the release or post that was delivered.",
    doesNotProve: "That the link is on the live page right now: CloudPush did not check the live page (or its check could not confirm this exact target: a match on the money-site host alone does not count, and a check recorded before anchors were stored cannot confirm a target). Synthetic .invalid receipts are never verified.",
  },
  observed: {
    label: "Observed",
    tone: "ok",
    counted: true,
    proves: "That exact target address was found among the anchors on the page CloudPush published, when a verification fetched it (HTTP 200) after the delivery, at the time of that check.",
    doesNotProve: "That the link is still there later, that the page is indexed, or any ranking effect. A match on the host alone stays delivered (not inspected); a check recorded before anchors were stored cannot confirm a target.",
  },
  planned: {
    label: "Planned (not delivered)",
    tone: "neutral",
    counted: false,
    proves: "A publication that includes this link has been accepted and is queued or running.",
    doesNotProve: "That anything was delivered: no source URL exists yet. Never counted.",
  },
  uncertain: {
    label: "Uncertain",
    tone: "warn",
    counted: false,
    proves: "The provider call may have changed the destination but left no receipt, so this link may or may not exist.",
    doesNotProve: "That it was delivered or that it was not. Retrying blindly is not safe. Never counted.",
  },
  absent: {
    label: "Absent",
    tone: "bad",
    counted: false,
    proves: "A verification fetched the page (HTTP 200) after the delivery, recorded the anchors it saw, and this exact target address was not among them.",
    doesNotProve: "That the link will never appear, or anything about other pages or other targets. Never counted.",
  },
};

export const stateOf = (raw: unknown): LinkState | null => (typeof raw === "string" && (LINK_STATES as readonly string[]).includes(raw) ? (raw as LinkState) : null);

// ---- readable names for the derived categories --------------------------------------------------------------------------

const SOURCE_KINDS: Record<string, string> = {
  site_home: "Site home page",
  site_blog: "Site blog page",
  site_service: "Site service page",
  site_location: "Site location page",
  site_about: "Site about page",
  site_contact: "Site contact page",
  site_other: "Other site page",
  content_post: "Content-platform post",
};
const TARGET_KINDS: Record<string, string> = {
  money_site_home: "Money site home",
  money_page: "Registered money page",
  money_site_other: "Money site, other page",
  gbp_maps: "Google Maps / GBP link (registered)",
  maps_unregistered: "Maps link (not registered)",
  other_external: "Other external site",
};
const ANCHOR_TYPES: Record<string, string> = { image: "Image", naked_url: "Naked URL", configured: "Configured anchor", branded: "Branded", generic: "Generic", descriptive: "Descriptive", none: "No anchor text" };
const FORMS: Record<string, string> = { html_anchor: "HTML link", markdown_link: "Markdown link", bare_url: "Plain-text URL (the platform decides whether it is clickable)" };
const FOLLOW: Record<string, string> = { followed: "Followed", restricted: "Restricted (nofollow, ugc or sponsored)", unknown: "Unknown" };
const PUB_STATES: Record<string, { label: string; tone: "ok" | "warn" | "bad" | "neutral" }> = {
  delivered: { label: "Delivered", tone: "ok" },
  queued: { label: "Queued, not delivered", tone: "neutral" },
  failed: { label: "Failed", tone: "bad" },
  needs_reconciliation: { label: "Needs reconciliation", tone: "warn" },
  // A previous account delivered; nothing is delivered for the current one and no attempt for it is running. Not a failure, not a delivery for this account.
  previous_account_only: { label: "Delivered under a previous account only", tone: "warn" },
};
const CURRENT_ATTEMPTS: Record<string, string> = {
  none: "no attempt for the current account",
  pinned_to_previous_account: "latest job pinned to a previous account (it will be refused)",
  queued: "queued",
  running: "running",
  retrying: "retrying",
  done: "done",
  refused_no_effect: "refused, nothing sent",
  dead_effect_unknown: "ended with an unknown effect",
};
export const currentAttemptLabel = (k: string | null): string | null => (k === null ? null : (ownValue(CURRENT_ATTEMPTS, k) ?? tidy(k, 60)));

/** The readable name of a derived category, or the raw (tidied) value when this page does not know it. */
export const sourceKindLabel = (k: string | null) => (k === null ? "Unknown source type" : (ownValue(SOURCE_KINDS, k) ?? tidy(k, 40)));
export const targetKindLabel = (k: string | null) => (k === null ? "Unknown target type" : (ownValue(TARGET_KINDS, k) ?? tidy(k, 40)));
export const anchorTypeLabel = (k: string | null) => (k === null ? "Unknown" : (ownValue(ANCHOR_TYPES, k) ?? tidy(k, 40)));
export const formLabel = (k: string | null) => (k === null ? "Unknown form" : (ownValue(FORMS, k) ?? tidy(k, 40)));
export const followLabel = (k: string | null) => (k === null ? "Unknown" : (ownValue(FOLLOW, k) ?? tidy(k, 40)));
export const publicationStateLabel = (s: string | null) => (s === null ? { label: "Unknown", tone: "neutral" as const } : (ownValue(PUB_STATES, s) ?? { label: tidy(s, 40), tone: "neutral" as const }));

// ---- counts ---------------------------------------------------------------------------------------------------------------

export interface Counts {
  /** Counted = delivered (not inspected) + observed. The only figure that may be called "created links". */
  active: number;
  deliveredUninspected: number;
  observed: number;
  /** Listed but never counted. */
  planned: number;
  uncertain: number;
  absent: number;
}

export function readCounts(v: unknown): Counts {
  const c = isObject(v) ? v : {};
  return {
    active: countOf(c.active),
    deliveredUninspected: countOf(c.deliveredUninspected),
    observed: countOf(c.observed),
    planned: countOf(c.planned),
    uncertain: countOf(c.uncertain),
    absent: countOf(c.absent),
  };
}

/** A facet map (`{ money_page: 2, ... }`) as sorted rows; non-numeric and non-positive values are dropped. Facets overlap: never sum them. */
export function readFacet(v: unknown): { key: string; count: number }[] {
  if (!isObject(v)) return [];
  return Object.entries(v)
    .map(([key, n]) => ({ key: tidy(key, 60), count: countOf(n) }))
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
}

// ---- one link -------------------------------------------------------------------------------------------------------------

export interface LinkView {
  id: string;
  /** Null when the server sent a state this page does not know: shown as given, never counted by this page. */
  state: LinkState | null;
  stateRaw: string;
  /** What the server says about counting. The page never counts a link whose state is not delivered_uninspected or observed. */
  counted: boolean;
  source: { url: UrlView | null; path: string | null; host: string | null; platform: string | null; platformCategory: string | null; kind: string | null };
  target: { href: UrlView | null; comparisonUrl: UrlView | null; kind: string | null; label: string | null; groupedBy: string | null };
  anchor: { text: string | null; type: string | null; hasImage: boolean; imageAlt: string | null };
  rel: { tokens: string[] | null; followType: string | null; basis: string | null; observedTokens: string[] | null; observedAt: string | null };
  form: string | null;
  repeatsOnPage: number;
  evidenceBasis: string | null;
  inspection: { result: string | null; httpStatus: number | null; checkedAt: string | null; note: string | null } | null;
  publications: number;
}

const tokens = (v: unknown): string[] | null => (Array.isArray(v) ? v.filter((t): t is string => typeof t === "string").map((t) => tidy(t, 40)) : null);

export function readLink(raw: unknown): LinkView | null {
  if (!isObject(raw)) return null;
  const id = str(raw.id);
  if (id === null) return null;
  const source = isObject(raw.source) ? raw.source : {};
  const target = isObject(raw.target) ? raw.target : {};
  const anchor = isObject(raw.anchor) ? raw.anchor : {};
  const rel = isObject(raw.rel) ? raw.rel : {};
  const observed = isObject(rel.observed) ? rel.observed : null;
  const insp = isObject(raw.inspection) ? raw.inspection : null;
  const evidence = isObject(raw.evidence) ? raw.evidence : {};
  return {
    id: tidy(id, 80),
    state: stateOf(raw.state),
    stateRaw: tidy(raw.state, 40),
    counted: raw.counted === true,
    source: {
      url: url(source.url),
      path: str(source.path) ? tidy(source.path, 300) : null,
      host: str(source.host) ? tidy(source.host, 200) : null,
      platform: str(source.platform) ? tidy(source.platform, 60) : null,
      platformCategory: str(source.platformCategory) ? tidy(source.platformCategory, 40) : null,
      kind: str(source.kind) ? tidy(source.kind, 40) : null,
    },
    target: {
      href: url(target.href),
      comparisonUrl: url(target.comparisonUrl),
      kind: str(target.kind) ? tidy(target.kind, 40) : null,
      label: str(target.label) ? tidy(target.label, 200) : null,
      groupedBy: str(target.groupedBy) ? tidy(target.groupedBy, 20) : null,
    },
    anchor: { text: typeof anchor.text === "string" ? tidy(anchor.text, 300) : null, type: str(anchor.type) ? tidy(anchor.type, 40) : null, hasImage: anchor.hasImage === true, imageAlt: str(anchor.imageAlt) ? tidy(anchor.imageAlt, 200) : null },
    rel: {
      tokens: tokens(rel.tokens),
      followType: str(rel.followType) ? tidy(rel.followType, 40) : null,
      basis: str(rel.basis) ? tidy(rel.basis, 60) : null,
      observedTokens: observed ? tokens(observed.tokens) : null,
      observedAt: observed ? (formatEpoch(observed.checkedAt)?.text ?? null) : null,
    },
    form: str(raw.form) ? tidy(raw.form, 40) : null,
    repeatsOnPage: Math.max(1, countOf(raw.repeatsOnPage)),
    evidenceBasis: str(evidence.basis) ? tidy(evidence.basis, 60) : null,
    inspection: insp
      ? { result: str(insp.result) ? tidy(insp.result, 40) : null, httpStatus: typeof insp.httpStatus === "number" && Number.isFinite(insp.httpStatus) ? insp.httpStatus : null, checkedAt: formatEpoch(insp.checkedAt)?.text ?? null, note: str(insp.note) ? tidy(insp.note, 300) : null }
      : null,
    publications: Array.isArray(raw.publications) ? raw.publications.length : 0,
  };
}

/** What the rel attribute says, in words an owner can read. "Unknown" is said plainly, never shown as an empty rel. */
export function relText(l: LinkView): string {
  if (l.rel.tokens === null) return "rel unknown: the platform decides";
  if (l.rel.tokens.length === 0) return "no rel attribute";
  return `rel="${l.rel.tokens.join(" ")}"`;
}

// ---- targets, publications, history ---------------------------------------------------------------------------------------

export interface TargetView {
  targetUrl: UrlView | null;
  kind: string | null;
  label: string | null;
  counts: Counts & { sourcePages: number; sourceHosts: number };
}

export function readTarget(raw: unknown): TargetView | null {
  if (!isObject(raw)) return null;
  const c = isObject(raw.counts) ? raw.counts : {};
  const targetUrl = url(raw.targetUrl);
  if (targetUrl === null) return null;
  return { targetUrl, kind: str(raw.kind) ? tidy(raw.kind, 40) : null, label: str(raw.label) ? tidy(raw.label, 200) : null, counts: { ...readCounts(c), sourcePages: countOf(c.sourcePages), sourceHosts: countOf(c.sourceHosts) } };
}

export interface PublicationView {
  id: string;
  title: string;
  platform: string | null;
  platformCategory: string | null;
  state: string | null;
  evidence: "established" | "unconfirmed" | "none" | "unknown";
  currentReceipts: number;
  previousAccountReceipts: number;
  links: Counts;
  note: string | null;
  pinnedToCurrentAccount: boolean | null;
  /** A previous account/revision delivered this publication (history only, never counted). Null on servers that do not say. */
  previousAccountDelivered: boolean | null;
  /** The latest attempt for the CURRENT account, as the server words it. Null on servers that do not say. */
  currentAttempt: string | null;
}

export function readPublication(raw: unknown): PublicationView | null {
  if (!isObject(raw)) return null;
  const receipts = isObject(raw.receipts) ? raw.receipts : {};
  const job = isObject(raw.latestJob) ? raw.latestJob : null;
  const ev = raw.evidence;
  return {
    id: tidy(str(raw.publicationId) ?? "", 120),
    title: str(raw.masterTitle) ? tidy(raw.masterTitle, 300) : "(untitled article)",
    platform: str(raw.platform) ? tidy(raw.platform, 60) : null,
    platformCategory: str(raw.platformCategory) ? tidy(raw.platformCategory, 40) : null,
    state: str(raw.state) ? tidy(raw.state, 40) : null,
    evidence: ev === "established" || ev === "unconfirmed" || ev === "none" ? ev : "unknown",
    currentReceipts: Array.isArray(receipts.current) ? receipts.current.length : 0,
    previousAccountReceipts: Array.isArray(receipts.previousAccount) ? receipts.previousAccount.length : 0,
    links: readCounts(raw.links),
    note: str(raw.note) ? tidy(raw.note, 400) : null,
    pinnedToCurrentAccount: job && typeof job.pinnedToCurrentAccount === "boolean" ? job.pinnedToCurrentAccount : null,
    previousAccountDelivered: typeof raw.previousAccountDelivered === "boolean" ? raw.previousAccountDelivered : null,
    currentAttempt: str(raw.currentAttempt) ? tidy(raw.currentAttempt, 60) : null,
  };
}

export interface HistoryLinkView {
  sourceUrl: UrlView | null;
  href: UrlView | null;
  anchorText: string | null;
  rel: string[] | null;
}
export interface HistoryReceiptView {
  kind: "previous_account" | "superseded_release";
  receiptId: string;
  platform: string | null;
  providerUrl: UrlView | null;
  deliveredAt: string | null;
  accountId: string | null;
  accountRevision: number | null;
  reason: string | null;
  evidence: string | null;
  links: HistoryLinkView[];
}

export function readHistoryReceipt(raw: unknown): HistoryReceiptView | null {
  if (!isObject(raw)) return null;
  return {
    kind: raw.kind === "superseded_release" ? "superseded_release" : "previous_account",
    receiptId: tidy(str(raw.receiptId) ?? "", 80),
    platform: str(raw.platform) ? tidy(raw.platform, 60) : null,
    providerUrl: url(raw.providerUrl),
    deliveredAt: formatEpoch(raw.deliveredAt)?.text ?? null,
    accountId: str(raw.accountId) ? tidy(raw.accountId, 80) : null,
    accountRevision: typeof raw.accountRevision === "number" && Number.isFinite(raw.accountRevision) ? raw.accountRevision : null,
    reason: str(raw.reason) ? tidy(raw.reason, 500) : null,
    evidence: str(raw.evidence) ? tidy(raw.evidence, 60) : null,
    links: (Array.isArray(raw.links) ? raw.links : []).filter(isObject).map((l) => ({ sourceUrl: url(l.sourceUrl), href: url(l.href), anchorText: typeof l.anchorText === "string" ? tidy(l.anchorText, 300) : null, rel: tokens(l.rel) })),
  };
}

// ---- the whole per-client report ------------------------------------------------------------------------------------------

export interface ClientLinksView {
  clientId: string;
  clientSlug: string;
  /** Business name (current servers); empty on older ones, where the page names the client by slug. */
  clientName: string;
  generatedAt: string | null;
  rules: { states: Partial<Record<LinkState, string>>; counting: string | null; scope: string | null; currentAccount: string | null; dedupeKey: string | null };
  totals: Counts & { links: number; sourcePages: number; sourceHosts: number; targets: number; byTargetKind: { key: string; count: number }[]; bySourceKind: { key: string; count: number }[] };
  targets: TargetView[];
  links: LinkView[];
  /** Rows the page could not read, counted so they are never silently dropped. */
  droppedLinks: number;
  publications: PublicationView[];
  history: { receipts: HistoryReceiptView[]; note: string | null };
  warnings: string[];
}

export function parseClientLinks(body: unknown): ClientLinksView | null {
  if (!isObject(body) || !isObject(body.links)) return null;
  const b = body.links;
  const clientId = str(b.clientId);
  if (clientId === null) return null;
  const rules = isObject(b.rules) ? b.rules : {};
  const ruleStates = isObject(rules.states) ? rules.states : {};
  const totals = isObject(b.totals) ? b.totals : {};
  const hist = isObject(b.history) ? b.history : {};
  const links: LinkView[] = [];
  let droppedLinks = 0;
  for (const raw of Array.isArray(b.links) ? b.links : []) {
    const l = readLink(raw);
    if (l) links.push(l);
    else droppedLinks += 1;
  }
  const states: Partial<Record<LinkState, string>> = {};
  for (const s of LINK_STATES) if (str(ruleStates[s])) states[s] = tidy(ruleStates[s], 600);
  return {
    clientId: tidy(clientId, 80),
    clientSlug: str(b.clientSlug) ? tidy(b.clientSlug, 120) : "",
    clientName: str(b.clientName) ? tidy(b.clientName, 300) : "",
    generatedAt: formatEpoch(b.generatedAt)?.text ?? null,
    rules: { states, counting: str(rules.counting) ? tidy(rules.counting, 800) : null, scope: str(rules.scope) ? tidy(rules.scope, 800) : null, currentAccount: str(rules.currentAccount) ? tidy(rules.currentAccount, 800) : null, dedupeKey: str(rules.dedupeKey) ? tidy(rules.dedupeKey, 800) : null },
    totals: { ...readCounts(totals), links: countOf(totals.links), sourcePages: countOf(totals.sourcePages), sourceHosts: countOf(totals.sourceHosts), targets: countOf(totals.targets), byTargetKind: readFacet(totals.byTargetKind), bySourceKind: readFacet(totals.bySourceKind) },
    targets: (Array.isArray(b.targets) ? b.targets : []).map(readTarget).filter((t): t is TargetView => t !== null),
    links,
    droppedLinks,
    publications: (Array.isArray(b.publications) ? b.publications : []).map(readPublication).filter((p): p is PublicationView => p !== null),
    history: { receipts: (Array.isArray(hist.receipts) ? hist.receipts : []).map(readHistoryReceipt).filter((r): r is HistoryReceiptView => r !== null), note: str(hist.note) ? tidy(hist.note, 500) : null },
    warnings: (Array.isArray(b.warnings) ? b.warnings : []).filter((w): w is string => typeof w === "string").map((w) => tidy(w, 600)),
  };
}

// ---- honesty checks the page makes on what it was given ----------------------------------------------------------------------

/** The page's own count of the links it was given, by state, and how many of them it considers counted. It never invents a state. */
export function tallyLinks(links: readonly LinkView[]): { byState: Record<LinkState, number>; unknownState: number; counted: number; flaggedCountedButNeverCounted: number; notFlaggedButCounted: number } {
  const byState: Record<LinkState, number> = { delivered_uninspected: 0, observed: 0, planned: 0, uncertain: 0, absent: 0 };
  let unknownState = 0;
  let counted = 0;
  let flaggedCountedButNeverCounted = 0;
  let notFlaggedButCounted = 0;
  for (const l of links) {
    if (l.state === null) {
      unknownState += 1;
      if (l.counted) flaggedCountedButNeverCounted += 1;
      continue;
    }
    byState[l.state] += 1;
    const shouldCount = STATE_META[l.state].counted;
    if (l.counted && shouldCount) counted += 1;
    if (l.counted && !shouldCount) flaggedCountedButNeverCounted += 1;
    if (!l.counted && shouldCount) notFlaggedButCounted += 1;
  }
  return { byState, unknownState, counted, flaggedCountedButNeverCounted, notFlaggedButCounted };
}

/**
 * Reasons to distrust the numbers, as visible sentences (empty when everything agrees). `filtered` is true while the list is
 * narrowed by state: the server's totals always describe everything, so the list can then legitimately be shorter.
 * Nothing here changes a figure; it only says when two figures that must agree do not.
 */
export function numberProblems(view: ClientLinksView, filtered: boolean): string[] {
  const t = tallyLinks(view.links);
  const out: string[] = [];
  if (t.flaggedCountedButNeverCounted > 0) out.push(`${t.flaggedCountedButNeverCounted} ${t.flaggedCountedButNeverCounted === 1 ? "link is" : "links are"} marked as counted by the server although its state (planned, uncertain, absent or unknown) is never counted. Those are not included in any counted number here.`);
  if (t.notFlaggedButCounted > 0) out.push(`${t.notFlaggedButCounted} ${t.notFlaggedButCounted === 1 ? "link is" : "links are"} delivered or observed but not marked as counted by the server.`);
  if (view.totals.active !== view.totals.deliveredUninspected + view.totals.observed) out.push(`The server's counted total (${view.totals.active}) is not delivered (not inspected) plus observed (${view.totals.deliveredUninspected} + ${view.totals.observed}).`);
  if (!filtered && view.droppedLinks === 0) {
    if (t.counted !== view.totals.active) out.push(`The counted total says ${view.totals.active} but the list below has ${t.counted} counted ${t.counted === 1 ? "link" : "links"}.`);
    for (const s of ["planned", "uncertain", "absent"] as const) {
      if (t.byState[s] !== view.totals[s]) out.push(`The total says ${view.totals[s]} ${s} but the list has ${t.byState[s]}.`);
    }
  }
  if (view.droppedLinks > 0) out.push(`${view.droppedLinks} ${view.droppedLinks === 1 ? "row" : "rows"} in the server's reply could not be read and ${view.droppedLinks === 1 ? "was" : "were"} left out of the list.`);
  return out;
}

/** True when the report cannot say which links a delivery contained: show a visible notice instead of what looks like zero links. */
export function unconfirmedPublications(view: ClientLinksView): PublicationView[] {
  return view.publications.filter((p) => p.evidence === "unconfirmed");
}

export const UNCONFIRMED_TEXT = "CloudPush delivered this but could not confirm which links it contained, so none are listed or counted. This is not a count of zero.";

/** Client-side narrowing of the loaded list (source, target and anchor). */
export function filterLinks(links: readonly LinkView[], query: string): LinkView[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...links];
  return links.filter((l) => [l.source.url?.text, l.source.path, l.target.href?.text, l.target.comparisonUrl?.text, l.anchor.text, l.target.label].some((s) => typeof s === "string" && s.toLowerCase().includes(q)));
}

export const STATE_FILTERS = ["all", ...LINK_STATES] as const;
export type StateFilter = (typeof STATE_FILTERS)[number];
export const parseStateFilter = (raw: unknown): StateFilter => (typeof raw === "string" && (STATE_FILTERS as readonly string[]).includes(raw) ? (raw as StateFilter) : "all");

// ---- all-clients summary ------------------------------------------------------------------------------------------------------

export interface SummaryClientView {
  clientId: string;
  slug: string;
  businessName: string;
  counts: Counts;
  sourcePages: number;
  sourceHosts: number;
  targets: number;
  publications: number;
  previousAccountReceipts: number;
  supersededReleaseReceipts: number;
  warnings: number;
}
export interface SummaryView {
  clients: SummaryClientView[];
  dropped: number;
  totalsThisPage: Counts;
  page: { total: number; limit: number; offset: number; returned: number; hasMore: boolean } | null;
}

export function parseSummary(body: unknown): SummaryView | null {
  if (!isObject(body) || !Array.isArray(body.clients)) return null;
  const clients: SummaryClientView[] = [];
  let dropped = 0;
  for (const raw of body.clients) {
    const id = isObject(raw) ? str(raw.clientId) : null;
    if (!isObject(raw) || id === null) {
      dropped += 1;
      continue;
    }
    clients.push({
      clientId: tidy(id, 80),
      slug: str(raw.slug) ? tidy(raw.slug, 120) : "",
      businessName: str(raw.businessName) ? tidy(raw.businessName, 300) : "(unnamed client)",
      counts: readCounts(raw.counts),
      sourcePages: countOf(raw.sourcePages),
      sourceHosts: countOf(raw.sourceHosts),
      targets: countOf(raw.targets),
      publications: countOf(raw.publications),
      previousAccountReceipts: countOf(raw.previousAccountReceipts),
      supersededReleaseReceipts: countOf(raw.supersededReleaseReceipts),
      warnings: countOf(raw.warnings),
    });
  }
  const p = isObject(body.page) ? body.page : null;
  return {
    clients,
    dropped,
    totalsThisPage: readCounts(body.totalsThisPage),
    page: p ? { total: countOf(p.total), limit: Math.max(1, countOf(p.limit)), offset: countOf(p.offset), returned: countOf(p.returned), hasMore: p.hasMore === true } : null,
  };
}

/** The sum of the listed clients' counted links, to compare with the server's "this page" total. Never used as the headline. */
export function sumActive(clients: readonly SummaryClientView[]): number {
  return clients.reduce((n, c) => n + c.counts.active, 0);
}

// ---- source pages and the "Re-check" action ------------------------------------------------------------------------------------

/** The local synthetic provider answers with https://<slug>.synthetic.invalid/. That name can never resolve, so such a page is never verified. */
export const SYNTHETIC_SUFFIX = ".synthetic.invalid";
export function isSyntheticPage(urlText: string | null | undefined): boolean {
  if (!urlText) return false;
  try {
    return new URL(urlText).hostname.toLowerCase().endsWith(SYNTHETIC_SUFFIX);
  } catch {
    return false;
  }
}

export interface SourcePageView {
  /** The exact source URL (the page CloudPush published). */
  url: string;
  urlView: UrlView;
  kind: string | null;
  platform: string | null;
  synthetic: boolean;
  /** Links on this page by state (only the states the server named). */
  byState: Partial<Record<LinkState, number>>;
  total: number;
  /** The newest check recorded for any link on this page, as the server worded it. */
  lastCheck: { checkedAt: string | null; result: string | null; httpStatus: number | null } | null;
}

/** One entry per distinct source URL among links that have one (planned links have no source yet). */
export function sourcePages(links: readonly LinkView[]): SourcePageView[] {
  const pages = new Map<string, SourcePageView>();
  for (const l of links) {
    const u = l.source.url;
    if (!u) continue;
    let p = pages.get(u.text);
    if (!p) {
      p = { url: u.text, urlView: u, kind: l.source.kind, platform: l.source.platform, synthetic: isSyntheticPage(u.href ?? u.text), byState: {}, total: 0, lastCheck: null };
      pages.set(u.text, p);
    }
    p.total += 1;
    if (l.state) p.byState[l.state] = (p.byState[l.state] ?? 0) + 1;
    if (l.inspection && (!p.lastCheck || (l.inspection.checkedAt ?? "") > (p.lastCheck.checkedAt ?? ""))) p.lastCheck = { checkedAt: l.inspection.checkedAt, result: l.inspection.result, httpStatus: l.inspection.httpStatus };
  }
  return [...pages.values()];
}

/** GET /v1/verification?clientId=: deployments (id, url). Only the two fields the Re-check button needs are read. */
export function parseDeployments(body: unknown): Map<string, string> | null {
  if (!isObject(body) || !Array.isArray(body.verifications)) return null;
  const map = new Map<string, string>();
  for (const d of body.verifications) if (isObject(d) && str(d.id) && str(d.url)) map.set(d.url as string, d.id as string);
  return map;
}

export interface RecheckResult {
  httpStatus: number | null;
  /** Number of links the verifier recorded on the page; null when it recorded none (it did not get the page). */
  anchors: number | null;
  note: string | null;
}
/** POST /v1/verification/run/:id -> { outcome: { httpStatus, anchors, note, ... } }. Null when the reply is not recognisable (not a result). */
export function parseRecheck(body: unknown): RecheckResult | null {
  if (!isObject(body) || !isObject(body.outcome)) return null;
  const o = body.outcome;
  const status = typeof o.httpStatus === "number" && Number.isFinite(o.httpStatus) ? o.httpStatus : null;
  if (status === null) return null;
  return { httpStatus: status, anchors: Array.isArray(o.anchors) ? o.anchors.length : null, note: str(o.note) ? tidy(o.note, 300) : null };
}

/** What a re-check result means for the links, in words. Never says a link exists because a page was fetched: the states below say that. */
export function describeRecheck(r: RecheckResult): { tone: "ok" | "warn"; text: string } {
  if (r.httpStatus !== 200) return { tone: "warn", text: `The page could not be fetched (HTTP ${r.httpStatus || "no answer"}), so nothing was confirmed and the links stay as they were.${r.note ? ` ${r.note}` : ""}` };
  if (r.anchors === null) return { tone: "warn", text: `The page answered (HTTP 200) but the check recorded none of its links, so nothing could be confirmed.${r.note ? ` ${r.note}` : ""}` };
  return { tone: "ok", text: `Checked just now: HTTP 200, ${r.anchors} ${r.anchors === 1 ? "link" : "links"} found on the page. A link below is Observed only if its exact target address was among them, and Absent only if it was not; this says nothing about later.${r.note ? ` ${r.note}` : ""}` };
}
